import { getApiUrl } from "../../services/apiConfig.js";
import { prepareExternalVideos } from "./externalVideos";
/* ALT-17 — ponte central de aprendizagem. Não altera bancos de questões nem pontos. */
(() => {
  "use strict";

  // Keep request batches within the server limit; a slow connection may queue many answers.
  const MAX_ITEMS_PER_REQUEST = 100;
  const sessions = new Set(),
    api = getApiUrl("learning/"),
    uid = () => crypto.randomUUID(),
    hash = async (value) =>
      Array.from(
        new Uint8Array(
          await crypto.subtle.digest(
            "SHA-256",
            new TextEncoder().encode(value),
          ),
        ),
      )
        .map((item) => item.toString(16).padStart(2, "0"))
        .join("");
  const auth = () => {
    try {
      const parseResult = JSON.parse(
        sessionStorage.getItem("auth_session") || "null",
      );
      return parseResult?.tipo === "aluno" ? parseResult.data : null;
    } catch {
      return null;
    }
  };
  const context = (key) => {
    const splitResult = String(key).split(":");
    return ["conteudo", "simulador", "olimpiada"].includes(splitResult[0]) &&
      /^\d+$/.test(splitResult[1])
      ? {
          student: Number(splitResult[1]),
          aula: splitResult[2],
          guia: splitResult[3],
          bloco: splitResult.slice(4).join(":"),
        }
      : null;
  };
  const visible = (value) => {
    if (!value?.isConnected || document.hidden || !document.hasFocus())
      return false;
    const getBoundingClientRectResult = value.getBoundingClientRect();
    return (
      getBoundingClientRectResult.width > 0 &&
      getBoundingClientRectResult.height > 0 &&
      getBoundingClientRectResult.bottom > 0 &&
      getBoundingClientRectResult.top < window.innerHeight
    );
  };
  let owner = null,
    lastInput = Date.now();
  for (const optionalValue of [
    "pointerdown",
    "keydown",
    "scroll",
    "touchstart",
  ])
    document.addEventListener(optionalValue, () => (lastInput = Date.now()), {
      passive: true,
      capture: true,
    });
  async function request(path, index, data, signal) {
    const authResult = auth();
    if (
      !authResult?.learning_token ||
      Number(authResult.id) !== index.student
    ) {
      console.warn(
        "DM Learning session check: " +
          (!authResult
            ? "missing-student"
            : !authResult.learning_token
              ? "missing-signed-token"
              : "different-student"),
      );
      const instance = new Error("Entre novamente para salvar seu progresso.");
      instance.code = 401;
      throw instance;
    }
    const params = {
      aula: index.aula,
      guia: index.guia,
      bloco: index.bloco,
      revision: index.revision,
      kind: index.kind,
    };
    const res = await fetch(
      api + path + (data ? "" : "?" + new URLSearchParams(params)),
      {
        method: data ? "POST" : "GET",
        credentials: "same-origin",
        cache: "no-store",
        signal,
        keepalive: !!data && JSON.stringify(data).length < 60000,
        headers: {
          "Content-Type": "application/json",
          "X-Learning-Token": authResult.learning_token,
          "X-Student-Id": String(authResult.id),
        },
        body: data
          ? JSON.stringify({
              ...params,
              ...data,
            })
          : undefined,
      },
    );
    const json = await res.json();
    if (!res.ok) {
      console.warn("DM Learning response status: " + res.status);
      const instance = new Error(
        json.message || "Não foi possível salvar agora.",
      );
      instance.code = res.status;
      throw instance;
    }
    if (data) {
      try {
        window.dispatchEvent(new Event("dm-learning-changed"));
      } catch {}
    }
    if (data && Number(json.data?.task_bonus?.added_points_milli) > 0) {
      try {
        window.DMTaskBonusRanking?.refresh();
      } catch {
        console.warn("Não foi possível atualizar a pontuação exibida.");
      }
    }
    return json.data;
  }
  async function unwrap(html, signal) {
    const uploadsBase = new URL(getApiUrl("uploads/"), window.location.href);
    let base = uploadsBase.href;
    for (let index = 0; index < 3; index++) {
      const doc = new DOMParser().parseFromString(html, "text/html"),
        querySelectorResult = doc.querySelector("iframe[src]");
      if (
        !querySelectorResult ||
        doc.querySelector('[id$="wrapper"],article') ||
        doc.querySelectorAll("iframe").length !== 1
      )
        break;
      const src = querySelectorResult.getAttribute("src");
      const url = new URL(
        /^\/?api\/uploads\//.test(src) ? getApiUrl(src) : src,
        base,
      );
      if (
        url.origin !== uploadsBase.origin ||
        !url.pathname.startsWith(uploadsBase.pathname) ||
        !/^(conteudos|simuladores|olimpiada)\//.test(
          url.pathname.slice(uploadsBase.pathname.length),
        ) ||
        !url.pathname.endsWith(".html")
      )
        break;
      const fetchResult = await fetch(url, {
        signal,
        cache: "no-cache",
      });
      if (!fetchResult.ok)
        throw new Error("Não foi possível abrir a atividade.");
      html = await fetchResult.text();
      base = url.href;
    }
    return {
      html,
      base,
    };
  }
  function instrument(html, resume) {
    return html.replace(
      /(<script\b[^>]*>)([\s\S]*?)(<\/script>)/gi,
      (all, start, code, end) => {
        if (/\bsrc\s*=/.test(start)) return all;
        for (const [optionalValue, hook] of [
          [
            /\b(var|let|const)\s+(motorData|questions(?:_[A-Za-z0-9]+)*|questionsData)\s*=/g,
            "__dmBank",
          ],
          [
            /\b(var|let|const)\s+(bancoDeCartoesSalvos)\s*=/g,
            "__dmOriginalBank",
          ],
          [/\b(var|let|const)\s+(cartoesEmbaralhadosAtuais)\s*=/g, "__dmBank"],
          [/\b(var|let|const)\s+(quizState)\s*=/g, "__dmQuizState"],
          [
            /\b(var|let|const)\s+(userAnswers|answers|states(?:_[A-Za-z0-9]+)*|memoriaDosCartoes)\s*=/g,
            "__dmAnswers",
          ],
        ])
          code = code.replace(
            optionalValue,
            (value, decl, name) =>
              `window.${hook}=()=>${name};${decl} ${name}=`,
          );
        code = code.replace(
          /\b(var|let)\s+(current(?:Idx|Index)(?:_[A-Za-z0-9]+)*|posicaoDoCartaoAtual)\s*=\s*0\s*;/g,
          (value, decl, name) =>
            `window.__dmPosition=()=>${name};${decl} ${name}=Math.max(0,Number(window.__dmResume.position)||0);`,
        );
        return start + code + end;
      },
    );
  }
  // Executa ANTES dos scripts da aula. Nenhum token de login é enviado para o iframe.
  // Esta função é serializada por toString: deve ser autossuficiente também
  // depois do build. Object.assign evita helpers externos gerados para spread.
  function frameBoot(config) {
    const cfg = config;
    window.__dmResume = {
      position: cfg.saved.position || 0,
    };
    const stores = cfg.saved.state?.stores || {
      localStorage: {},
      sessionStorage: {},
    };
    const drafts = Object.assign({}, cfg.saved.state?.drafts || {});
    let touched = false,
      dirty = false,
      force = -1,
      lastPointer = Date.now(),
      attempts = [],
      baselines = new Map(),
      confirmations = new Set(),
      ready = false;
    function readBank() {
      try {
        return window.__dmBank?.() || window.__dmOriginalBank?.() || [];
      } catch {
        return [];
      }
    }
    function pack(value) {
      try {
        const parseResult = JSON.parse(value);
        if (Array.isArray(parseResult?.cartoesEmbaralhadosAtuais)) {
          const bank = window.__dmOriginalBank?.() || [];
          parseResult.__dmOrder = parseResult.cartoesEmbaralhadosAtuais.map(
            (item) =>
              bank.findIndex(
                (resultado) =>
                  JSON.stringify(resultado) === JSON.stringify(item),
              ),
          );
          if (parseResult.__dmOrder.every((item) => item >= 0))
            delete parseResult.cartoesEmbaralhadosAtuais;
        }
        return JSON.stringify(parseResult);
      } catch {
        return value;
      }
    }
    function unpack(value) {
      try {
        const parseResult = JSON.parse(value);
        if (Array.isArray(parseResult?.__dmOrder)) {
          const bank = window.__dmOriginalBank?.() || [];
          parseResult.cartoesEmbaralhadosAtuais = parseResult.__dmOrder
            .map((item) => bank[item])
            .filter(Boolean);
          delete parseResult.__dmOrder;
        }
        return JSON.stringify(parseResult);
      } catch {
        return value;
      }
    }
    for (const name of ["localStorage", "sessionStorage"]) {
      let memory = Object.assign({}, stores[name] || {});
      const proxy = {
        get length() {
          return Object.keys(memory).length;
        },
        key: (value) => Object.keys(memory)[value] ?? null,
        getItem: (value) => (value in memory ? unpack(memory[value]) : null),
        setItem(value, index) {
          memory[String(value)] = pack(String(index));
          stores[name] = Object.assign({}, memory);
          if (ready && touched) {
            dirty = true;
            queueMicrotask(scan);
          }
        },
        removeItem(value) {
          delete memory[String(value)];
          stores[name] = Object.assign({}, memory);
          if (ready && touched) dirty = true;
        },
        clear() {
          memory = {};
          stores[name] = {};
          if (ready && touched) dirty = true;
        },
      };
      Object.defineProperty(window, name, {
        configurable: false,
        get: () => proxy,
      });
    }
    const send = (data) =>
      window.parent.postMessage(
        Object.assign({
          type: "dm-learning",
          frameId: cfg.frameId,
          key: cfg.key,
        }, data),
        cfg.origin,
      );
    const qHash = (value) => {
      let index = 2166136261,
        indexAtual2 = 5381;
      for (let indexAtual3 = 0; indexAtual3 < value.length; indexAtual3++) {
        index = Math.imul(index ^ value.charCodeAt(indexAtual3), 16777619);
        indexAtual2 =
          Math.imul(indexAtual2, 33) ^ value.charCodeAt(indexAtual3);
      }
      return (
        (index >>> 0).toString(16).padStart(8, "0") +
        (indexAtual2 >>> 0).toString(16).padStart(8, "0")
      );
    };
    const fullHash = async (value) =>
      Array.from(
        new Uint8Array(
          await crypto.subtle.digest(
            "SHA-256",
            new TextEncoder().encode(value),
          ),
        ),
      )
        .map((item) => item.toString(16).padStart(2, "0"))
        .join("");
    let scanning = null,
      again = false;
    function scan() {
      if (!ready) return Promise.resolve();
      if (scanning) {
        again = true;
        return scanning;
      }
      const forced = force;
      force = -1;
      scanning = Promise.resolve()
        .then(async () => {
          const bank = readBank();
          let ans;
          let numericQuiz = false;
          try {
            ans = window.__dmAnswers?.();
          } catch {}
          // Modelo legado: -1 significa pendente; zero é uma alternativa válida.
          // A confirmação vem do feedback e do bloqueio das alternativas, não do clique.
          if (!Array.isArray(ans) && cfg.kind === "fechada") {
            try {
              const quiz = window.__dmQuizState?.();
              if (Array.isArray(quiz?.answers)) {
                numericQuiz = true;
                ans = quiz.answers.map((selectedAnswer, index) => ({
                  checked: Number.isInteger(selectedAnswer) && selectedAnswer >= 0 &&
                    Number.isInteger(bank[index]?.correctIndex),
                  selectedAnswer,
                  hintUsed: !!quiz.hintsUnlocked?.[index],
                }));
              }
            } catch {}
          }
          if (!Array.isArray(ans)) {
            ans = [];
            for (const val of Object.values(stores.localStorage)) {
              try {
                const parseResult = JSON.parse(val);
                if (Array.isArray(parseResult)) {
                  ans = parseResult;
                  break;
                }
                if (Array.isArray(parseResult?.memoriaDosCartoes)) {
                  ans = parseResult.memoriaDosCartoes;
                  break;
                }
              } catch {}
            }
          }
          // Alguns modelos salvam as alternativas e suas confirmações em
          // listas separadas. A alternativa escolhida ainda não é uma resposta.
          let confirmedAnswers;
          if (cfg.kind === "fechada") {
            // Esta função vai para o iframe por toString. Desestruturação de
            // pares gera um helper do Babel fora dela no build de produção.
            for (const entry of Object.entries(stores.localStorage)) {
              const key = entry[0], value = entry[1];
              if (!/(?:^|[_:-])conf(?:irm(?:ed|ations?)?)?(?:$|[_:-])/i.test(key))
                continue;
              try {
                const flags = JSON.parse(value);
                if (
                  Array.isArray(flags) &&
                  flags.length === bank.length &&
                  flags.every((flag) => typeof flag === "boolean")
                ) {
                  confirmedAnswers = flags;
                  break;
                }
              } catch {}
            }
          }
          for (let index = 0; index < ans.length; index++) {
            const item = ans[index],
              itemAtual2 = bank[index];
            if (!item || !itemAtual2) continue;
            const complete = typeof item === "string"
              ? !confirmedAnswers || confirmedAnswers[index] === true
              : item.checked === true;
            if (!complete) continue;
            const sig = JSON.stringify(item),
              qHashResult = qHash(JSON.stringify(itemAtual2)),
              old = baselines.get(qHashResult);
            if (numericQuiz && touched) {
              const feedback = document.getElementById("feedback-area");
              const corrected = feedback && !feedback.hidden &&
                !feedback.classList.contains("hidden") &&
                document.querySelector("#options-group button[disabled]");
              if (index !== position() || !corrected) continue;
            }
            baselines.set(qHashResult, sig);
            if (
              touched &&
              (forced === index || old !== sig || confirmations.has(index)) &&
              (cfg.kind !== "fechada" ||
                (confirmedAnswers || typeof item === "object"
                  ? old !== sig
                  : confirmations.has(index)))
            ) {
              const indexScored =
                cfg.kind === "fechada" &&
                Number.isInteger(itemAtual2.correctIndex) &&
                Number.isInteger(item.selectedAnswer);
              const score = indexScored
                ? item.selectedAnswer === itemAtual2.correctIndex
                  ? 10
                  : 0
                : typeof item === "string"
                  ? item === itemAtual2.answer
                    ? 10
                    : 0
                  : Number(item.score) || 0;
              attempts.push({
                key: await fullHash(JSON.stringify(itemAtual2)),
                index: index,
                score: Math.max(0, Math.min(10, score)),
                answer:
                  typeof item === "string"
                    ? item
                    : (item.text ?? Object.assign({
                        score: item.score,
                        hintUsed: !!item.hintUsed,
                      }, indexScored
                          ? {
                              selectedAnswer: item.selectedAnswer,
                              correctIndex: itemAtual2.correctIndex,
                              scoreScale: "answer-index-v1",
                            }
                          : {})),
                stamp: crypto.randomUUID(),
              });
              confirmations.delete(index);
              dirty = true;
            }
          }
        })
        .finally(() => {
          scanning = null;
          if (again) {
            again = false;
            queueMicrotask(scan);
          }
        });
      return scanning;
    }
    function position() {
      try {
        return Math.max(0, Number(window.__dmPosition?.()) || 0);
      } catch {
        return 0;
      }
    }
    function saveDraft() {
      const input = document.getElementById("student-input");
      if (input && touched) drafts[position()] = input.value;
    }
    function restoreDraft() {
      const input = document.getElementById("student-input");
      if (input && drafts[position()] !== undefined && !input.value)
        input.value = drafts[position()];
    }
    function snapshot() {
      saveDraft();
      return {
        state: {
          stores,
          scrollY: window.scrollY,
          drafts: Object.assign({}, drafts),
        },
        position: position(),
      };
    }
    async function pulse() {
      if (!ready) return;
      // Correções podem terminar depois do clique (por exemplo, uma avaliação assíncrona).
      await scan();
      const payload = Object.assign(snapshot(), {
        items: attempts.splice(0),
        dirty,
        active: Date.now() - lastPointer < 120000,
      });
      dirty = false;
      send({
        event: "snapshot",
        payload,
      });
    }
    document.addEventListener(
      "pointerdown",
      () => {
        touched = true;
        lastPointer = Date.now();
      },
      true,
    );
    document.addEventListener(
      "keydown",
      () => {
        touched = true;
        lastPointer = Date.now();
      },
      true,
    );
    document.addEventListener(
      "click",
      (event) => {
        const closestResult = event.target.closest("button");
        if (!closestResult) return;
        const id = closestResult.id;
        if (
          /^btn-(confirm|check)(?:_\d+)?$/.test(id) ||
          /btn-(correct|wrong|partial|almost-correct|almost-wrong)-flashcards/.test(
            id,
          )
        ) {
          force = position();
          // A confirmação pode abrir um diálogo ou concluir a correção depois
          // deste clique. Mantê-la até a resposta estar efetivamente concluída.
          if (cfg.kind === "fechada") confirmations.add(force);
        }
        saveDraft();
        setTimeout(() => {
          restoreDraft();
          if (touched) {
            dirty = true;
            scan().then(pulse);
          }
        }, 0);
      },
      true,
    );
    document.addEventListener(
      "input",
      () => {
        touched = true;
        dirty = true;
        saveDraft();
        lastPointer = Date.now();
        clearTimeout(window.__dmInputTimer);
        window.__dmInputTimer = setTimeout(pulse, 450);
      },
      true,
    );
    document.addEventListener(
      "scroll",
      () => {
        touched = true;
        dirty = true;
        lastPointer = Date.now();
      },
      {
        passive: true,
      },
    );
    window.addEventListener("load", () => {
      ready = true;
      scan().then(() => {
        const draft = cfg.saved.state?.drafts?.[position()],
          input = document.getElementById("student-input");
        if (input && draft && !input.value) input.value = draft;
        if (cfg.saved.state?.scrollY)
          window.scrollTo(0, cfg.saved.state.scrollY);
        send({
          event: "ready",
        });
      });
    });
    let banner;
    window.addEventListener("message", (response) => {
      if (
        response.source !== window.parent ||
        response.origin !== cfg.origin ||
        response.data?.frameId !== cfg.frameId
      )
        return;
      const data = response.data;
      if (data.type === "dm-learning-status") {
        if (!banner) {
          banner = document.createElement("div");
          banner.setAttribute("role", "status");
          banner.style.cssText =
            "font:12px/1.4 system-ui;color:#276452;padding:7px 12px;background:#f3faf7";
          document.body.prepend(banner);
        }
        banner.textContent = data.text;
      }
      if (data.type === "dm-learning-pulse") pulse();
    });
    setInterval(pulse, 1500);
    window.addEventListener("pagehide", pulse);
    window.__dmExportNow = async () => {
      await scan();
      return Object.assign(snapshot(), {
        items: attempts.splice(0),
        dirty: dirty || touched,
        active: Date.now() - lastPointer < 120000,
      });
    };
  }
  class Session {
    constructor(value, saved) {
      Object.assign(this, {
        c: value,
        saved,
        version: saved.version || 0,
        ticket: saved.ticket,
        ticketAt: Date.now(),
        writer: uid(),
        seq: 0,
        busy: false,
        disposed: false,
        pending: [],
        state: saved.state,
        position: saved.position || 0,
        videoPosition: saved.videoPosition || 0,
        seconds: {
          page: 0,
          summary: 0,
          video: 0,
        },
        dirty: false,
        activity: false,
        last: performance.now(),
        lastReport: performance.now(),
        lastInteraction: Date.now(),
        inFlight: null,
      });
      sessions.add(this);
    }
    setStatus(text) {
      this.status?.(text);
    }
    async renew() {
      if (this.renewing) return this.renewing;
      this.renewing = request("focus", this.c, null)
        .then((result) => {
          this.ticket = result.ticket;
          this.ticketAt = Date.now();
        })
        .finally(() => (this.renewing = null));
      return this.renewing;
    }
    focus() {
      if (owner !== this) {
        owner = this;
        this.last = performance.now();
        this.renew().catch((error) => this.setStatus(error.message));
      } else if (Date.now() - this.ticketAt > 240000)
        this.renew().catch((error) => this.setStatus(error.message));
    }
    outKey() {
      return (
        "dm-learning-outbox:" +
        this.c.student +
        ":" +
        this.c.hash +
        ":" +
        this.c.revision
      );
    }
    hasPending() {
      return this.dirty || this.pending.length || this.seconds.page > 0;
    }
    persist() {
      try {
        if (this.inFlight || this.hasPending())
          localStorage.setItem(
            this.outKey(),
            JSON.stringify({
              request: this.inFlight,
              tail: {
                state: this.state,
                position: this.position,
                videoPosition: this.videoPosition,
                items: this.pending,
                seconds: this.seconds,
                dirty: this.dirty,
              },
            }),
          );
        else localStorage.removeItem(this.outKey());
      } catch {
        this.setStatus(
          "Não foi possível guardar a cópia de segurança neste aparelho.",
        );
      }
    }
    accept(payload) {
      this.state = payload.state;
      this.position = payload.position;
      this.activity = payload.active;
      this.dirty ||= payload.dirty;
      this.pending.push(...(payload.items || []));
      if (payload.dirty || payload.items?.length) {
        this.persist();
        this.flush();
      }
    }
    async flush() {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
      if (this.busy || (!this.inFlight && !this.hasPending())) return;
      this.busy = true;
      if (!this.inFlight) {
        this.inFlight = {
          writer: this.writer,
          seq: ++this.seq,
          ticket: this.ticket,
          version: this.version,
          state: this.state,
          position: this.position,
          videoPosition: this.videoPosition,
          items: this.pending.splice(0, MAX_ITEMS_PER_REQUEST),
          seconds: {
            ...this.seconds,
          },
        };
        this.dirty = false;
        this.seconds = {
          page: 0,
          summary: 0,
          video: 0,
        };
      }
      this.persist();
      this.setStatus("Salvando progresso…");
      try {
        if (!this.ticket || Date.now() - this.ticketAt > 240000)
          await this.renew();
        const payload = {
          ...this.inFlight,
          ticket: this.ticket,
        };
        const result = await request("state", this.c, payload);
        this.version = result.version;
        this.inFlight = null;
        this.persist();
        const items = result.items || [],
          correct = items.filter(
            (item) => Number(item.last_score) >= 9.999,
          ).length;
        this.setStatus(
          result.conflict
            ? "Outra guia atualizou este material. Reabra a atividade para retomar o estado mais recente."
            : `Progresso salvo${items.length ? " · " + items.length + " feitas · " + correct + " acertos" : ""}`,
        );
      } catch (error) {
        this.persist();
        this.setStatus(
          error.code === 401
            ? error.message
            : "Sem conexão: progresso guardado neste aparelho para reenviar.",
        );
        if (error.code === 409) this.ticketAt = 0;
      } finally {
        this.busy = false;
        if (this.hasPending() || this.inFlight) {
          if (!this.disposed && !this.retryTimer)
            this.retryTimer = setTimeout(() => this.flush(), 1200);
          else if (!this.inFlight) this.flush();
        }
      }
    }
    async recover() {
      let optionalValue;
      try {
        optionalValue = JSON.parse(
          localStorage.getItem(this.outKey()) || "null",
        );
      } catch {}
      if (!optionalValue) return;
      const old =
          optionalValue.request ||
          (optionalValue.writer ? optionalValue : null),
        tail = optionalValue.tail || {};
      this.inFlight = old;
      this.pending = tail.items || [];
      this.seconds = tail.seconds || {
        page: 0,
        summary: 0,
        video: 0,
      };
      this.dirty = !!tail.dirty || !!optionalValue.tail;
      // Older clients may have saved an oversized request that the server never accepted.
      // Retain its writer/sequence for the first batch and send the remaining items afterwards.
      if (
        Array.isArray(this.inFlight?.items) &&
        this.inFlight.items.length > MAX_ITEMS_PER_REQUEST
      ) {
        this.pending = [
          ...this.inFlight.items.slice(MAX_ITEMS_PER_REQUEST),
          ...this.pending,
        ];
        this.inFlight = {
          ...this.inFlight,
          items: this.inFlight.items.slice(0, MAX_ITEMS_PER_REQUEST),
        };
      }
      this.state = tail.state ?? old?.state ?? this.saved.state;
      this.position = tail.position ?? old?.position ?? this.saved.position;
      this.videoPosition =
        tail.videoPosition ?? old?.videoPosition ?? this.saved.videoPosition;
      this.saved = {
        ...this.saved,
        state: this.state,
        position: this.position,
        videoPosition: this.videoPosition,
      };
      await this.flush();
      if (!this.inFlight && !this.hasPending()) {
        const requestResult = await request("state", this.c);
        this.saved = requestResult;
        this.state = requestResult.state;
        this.position = requestResult.position;
        this.videoPosition = requestResult.videoPosition;
        this.version = requestResult.version;
      }
    }
    tick(now) {
      const minResult = Math.min(2, Math.max(0, (now - this.last) / 1000));
      this.last = now;
      if (
        this.disposed ||
        !visible(this.element) ||
        (this.isVisible && !this.isVisible())
      )
        return;
      const playing = this.playing?.();
      const active =
        playing || this.activity || Date.now() - lastInput < 120000;
      if (!active || (owner && owner !== this)) return;
      this.focus();
      this.seconds.page += minResult;
      if (playing) {
        this.seconds.video += minResult;
        this.videoPosition = this.getPosition?.() ?? this.videoPosition;
      } else if (this.c.kind === "resumo") this.seconds.summary += minResult;
      if (now - this.lastReport > 10000) {
        this.lastReport = now;
        this.persist();
        this.flush();
      }
    }
    dispose() {
      if (this.disposed) return;
      this.disposed = true;
      clearTimeout(this.retryTimer);
      this.persist();
      this.flush();
      sessions.delete(this);
      if (owner === this) owner = null;
      this.cleanup?.();
    }
  }
  setInterval(() => {
    const now = performance.now();
    const video = [...sessions].find(
      (item) =>
        visible(item.element) &&
        (!item.isVisible || item.isVisible()) &&
        item.playing?.(),
    );
    const candidate =
      video ||
      [...sessions].find(
        (item) =>
          visible(item.element) &&
          (!item.isVisible || item.isVisible()) &&
          item.c.kind === "resumo",
      ) ||
      [...sessions].find(
        (item) =>
          visible(item.element) && (!item.isVisible || item.isVisible()),
      );
    if (candidate && owner !== candidate) candidate.focus();
    for (const optionalValue of sessions) optionalValue.tick(now);
  }, 1000);
  window.addEventListener("visibilitychange", () => {
    if (document.hidden) {
      for (const optionalValue of sessions) optionalValue.flush();
      owner = null;
    } else lastInput = Date.now();
  });
  window.addEventListener("pagehide", () => {
    for (const optionalValue of sessions) optionalValue.flush();
  });
  async function prepare(html, key, signal, contentKind) {
    let contextResult = context(key);
    const unwrapped = await unwrap(html, signal);
    const bankSources = [
      ...unwrapped.html.matchAll(
        /(?:var|let|const)\s+(?:motorData|questions(?:_[A-Za-z0-9]+)*|questionsData|bancoDeCartoesSalvos)\s*=\s*/g,
      ),
    ].map((item) => {
      let start = item.index + item[0].length,
        depth = 0,
        quote = "",
        escaped = false,
        end = start;
      for (; end < unwrapped.html.length; end++) {
        const itemAtual2 = unwrapped.html[end];
        if (quote) {
          if (escaped) escaped = false;
          else if (itemAtual2 === "\\") escaped = true;
          else if (itemAtual2 === quote) quote = "";
          continue;
        }
        if (itemAtual2 === '\"' || itemAtual2 === "'" || itemAtual2 === "`")
          quote = itemAtual2;
        else if (itemAtual2 === "[" || itemAtual2 === "{") depth++;
        else if (itemAtual2 === "]" || itemAtual2 === "}") depth--;
        if (depth === 0 && itemAtual2 === ";") break;
      }
      return unwrapped.html.slice(start, end);
    });
    const revision = await hash(
      bankSources.length
        ? bankSources.join("\n")
        : unwrapped.html.replace(/<style\b[^>]*>[\s\S]*?<\/style>/gi, ""),
    );
    const root = new DOMParser().parseFromString(unwrapped.html, "text/html");
    const displayHtml = await prepareExternalVideos(unwrapped.html, signal);
    const activityKind =
      root.querySelector('[id^="dm-flashcards-wrapper"],#app-flashcards')
        ? "flashcard"
        : root.querySelector('[id^="dm-abertas-wrapper"]')
          ? "aberta"
          : root.querySelector('[id^="dm-fechadas-wrapper"],#app-simulado')
            ? "fechada"
            : null;
    // Um bloco de texto pode conter um exercício. O fallback “resumo” do
    // renderizador não deve substituir a modalidade identificada no HTML.
    const kind = ["fechada", "aberta", "flashcard"].includes(contentKind)
      ? contentKind
      : activityKind || contentKind || "resumo";
    if (!contextResult)
      return {
        html: displayHtml,
        session: null,
      };
    contextResult = {
      ...contextResult,
      kind,
      revision,
      hash: await hash(
        contextResult.aula +
          "|" +
          contextResult.guia +
          "|" +
          contextResult.bloco,
      ),
    };
    let saved = {
      state: null,
      position: 0,
      version: 0,
    };
    try {
      saved = await request("state", contextResult, null, signal);
    } catch (error) {
      if (error.name === "AbortError") throw error;
      saved.warning = error.message;
    }
    const instance = new Session(contextResult, saved);
    await instance.recover();
    return {
      html: instrument(
        (contentKind === "resumo"
          ? "<style>body{font-size:14px;line-height:1.8;color:#24323F}</style>"
          : "") + displayHtml,
        instance.saved,
      ),
      base: unwrapped.base,
      session: instance,
    };
  }
  function doc(html, base, frameId, index) {
    if (!index) return html;
    const cfg = {
      key: index.c.hash + ":" + index.c.revision,
      frameId,
      kind: index.c.kind,
      saved: index.saved,
      origin: window.location.origin,
    };
    const boot =
      "<script>(" +
      frameBoot.toString() +
      ")(" +
      JSON.stringify(cfg).replace(/</g, "\\u003c") +
      ");<\/script>";
    const value = '<base href="' + base.replace(/"/g, "&quot;") + '">' + boot;
    if (/<head[^>]*>/i.test(html))
      return html.replace(/<head([^>]*)>/i, "<head$1>" + value);
    return value + html;
  }
  async function attachVideo(value, key, outer = null) {
    const contextResult = context(key);
    if (!contextResult) return () => {};
    contextResult.kind = "video";
    contextResult.revision = await hash(value.src);
    contextResult.hash = await hash(
      contextResult.aula + "|" + contextResult.guia + "|" + contextResult.bloco,
    );
    const saved = await request("state", contextResult);
    if (!value.isConnected) return () => {};
    const instance = new Session(contextResult, saved);
    await instance.recover();
    instance.element = value;
    instance.isVisible = () => !outer || visible(outer);
    let player = null,
      playing = false,
      pos = 0;
    try {
      if (value.tagName === "VIDEO") {
        instance.playing = () =>
          !value.paused && !value.ended && !value.seeking;
        instance.getPosition = () => value.currentTime;
        if (instance.videoPosition) value.currentTime = instance.videoPosition;
      } else if (/youtube(?:-nocookie)?\.com/.test(value.src)) {
        if (!window.YT?.Player) {
          if (!window.__dmYTReady)
            window.__dmYTReady = new Promise((resolve, reject) => {
              const prior = window.onYouTubeIframeAPIReady;
              window.onYouTubeIframeAPIReady = () => {
                prior?.();
                resolve();
              };
              const element = document.createElement("script");
              element.src = "https://www.youtube.com/iframe_api";
              element.onerror = () => {
                window.__dmYTReady = null;
                element.remove();
                reject(
                  new Error(
                    "Não foi possível carregar o registro de tempo do YouTube.",
                  ),
                );
              };
              document.head.append(element);
            });
          await window.__dmYTReady;
        }
        if (!value.isConnected) {
          instance.dispose();
          return () => {};
        }
        player = new window.YT.Player(value, {
          events: {
            onReady: (event) => {
              if (instance.videoPosition) {
                event.target.seekTo(instance.videoPosition, true);
              }
              // O aluno pode iniciar a reprodução antes de a API do player ficar pronta.
              playing = event.target.getPlayerState() === 1;
              instance.activity = playing;
              if (playing) instance.focus();
            },
            onStateChange: (response) => {
              playing = response.data === 1;
              instance.activity = playing;
              if (playing) instance.focus();
              else instance.flush();
            },
          },
        });
        instance.playing = () => playing;
        // O SDK pode trocar o iframe original; medir o elemento que está na tela.
        instance.element = player.getIframe();
        instance.getPosition = () => {
          try {
            return player.getCurrentTime();
          } catch {
            return pos;
          }
        };
        instance.cleanup = () => {
          playing = false;
        };
      } else if (/player\.vimeo\.com/.test(value.src)) {
        if (!window.Vimeo?.Player) {
          if (!window.__dmVimeoReady)
            window.__dmVimeoReady = new Promise((resolve, reject) => {
              const script = document.createElement("script");
              script.src = "https://player.vimeo.com/api/player.js";
              script.onload = resolve;
              script.onerror = reject;
              document.head.append(script);
            });
          await window.__dmVimeoReady;
        }
        if (!value.isConnected) {
          instance.dispose();
          return () => {};
        }
        player = new window.Vimeo.Player(value);
        await player.ready();
        if (instance.videoPosition)
          await player.setCurrentTime(instance.videoPosition);
        playing = !(await player.getPaused());
        pos = await player.getCurrentTime();
        player.on("play", () => {
          playing = true;
          instance.focus();
        });
        player.on("pause", () => {
          playing = false;
          instance.flush();
        });
        player.on("ended", () => {
          playing = false;
          instance.flush();
        });
        player.on("timeupdate", (resultado) => (pos = resultado.seconds));
        player.on("seeking", () => (playing = false));
        player.on(
          "seeked",
          async () => (playing = !(await player.getPaused())),
        );
        instance.playing = () => playing;
        instance.getPosition = () => pos;
        if (playing) instance.focus();
        instance.cleanup = () => {
          playing = false;
          player.off("play");
          player.off("pause");
          player.off("ended");
          player.off("timeupdate");
          player.off("seeking");
          player.off("seeked");
        };
      }
    } catch (error) {
      instance.dispose();
      throw error;
    }
    return () => instance.dispose();
  }
  function videoUrl(url) {
    try {
      const urlAtual2 = new URL(url, window.location.href);
      if (/youtube(?:-nocookie)?\.com/.test(urlAtual2.hostname)) {
        urlAtual2.searchParams.set("enablejsapi", "1");
        urlAtual2.searchParams.set("origin", window.location.origin);
        urlAtual2.searchParams.set("playsinline", "1");
      }
      return urlAtual2.href;
    } catch {
      return url;
    }
  }
  function bindFrame(value, frameId, index) {
    if (!index) return () => {};
    const children = [];
    index.element = value;
    index.cleanup = () => children.forEach((clean) => clean());
    index.status = (text) => {
      if (value.contentWindow && value.isConnected)
        value.contentWindow.postMessage(
          {
            type: "dm-learning-status",
            frameId,
            text,
          },
          window.location.origin,
        );
    };
    const listener = (response) => {
      if (
        response.source !== value.contentWindow ||
        response.origin !== window.location.origin ||
        response.data?.frameId !== frameId ||
        response.data?.key !== index.c.hash + ":" + index.c.revision
      )
        return;
      if (response.data.event === "snapshot")
        index.accept(response.data.payload);
      if (response.data.event === "ready") {
        index.setStatus(
          index.saved.warning || "Seu progresso será salvo automaticamente.",
        );
        for (const optionalValue of value.contentDocument?.querySelectorAll(
          "iframe[src],video",
        ) || []) {
          const src = optionalValue.src || "";
          if (
            /youtube|player\.vimeo\.com/.test(src) ||
            optionalValue.tagName === "VIDEO"
          ) {
            if (optionalValue.tagName !== "VIDEO")
              optionalValue.src = videoUrl(src);
            attachVideo(
              optionalValue,
              [
                "conteudo",
                index.c.student,
                index.c.aula,
                index.c.guia,
                index.c.bloco,
              ].join(":"),
              value,
            ).then((clean) => {
              if (index.disposed) clean();
              else children.push(clean);
            });
          }
        }
      }
    };
    window.addEventListener("message", listener);
    return () => {
      try {
        const final = value.contentWindow?.__dmExportNow?.();
        if (final) {
          Promise.resolve(final)
            .then((payload) => index.accept(payload))
            .catch(() => {
              index.setStatus(
                "Não foi possível finalizar o registro desta atividade.",
              );
            });
        }
      } catch {}
      window.removeEventListener("message", listener);
      index.dispose();
    };
  }
  window.DMLearning = {
    prepare,
    doc,
    bindFrame,
    attachVideo,
    videoUrl,
    context,
    hash,
  };
})();
